import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { TrendingUp, TrendingDown, FlaskConical, AlertTriangle, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const fmtMoney = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  return `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(2)}`;
};
const fmtSigned = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  return `${n >= 0 ? '+' : '-'}$${Math.abs(n).toFixed(2)}`;
};

const CostSimulatorCard = ({ itemId, itemName, baseUom }) => {
  const { api } = useAuth();
  const [percent, setPercent] = useState(25);
  const [absoluteCost, setAbsoluteCost] = useState('');
  const [mode, setMode] = useState('percent'); // 'percent' | 'absolute'
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();

  const runSim = async () => {
    setLoading(true);
    try {
      const body = mode === 'absolute'
        ? { absolute_unit_cost: absoluteCost === '' ? null : parseFloat(absoluteCost) }
        : { percent_change: parseFloat(percent) };
      const { data } = await api().post(`/items/${itemId}/cost-simulation`, body);
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Simulation failed');
    } finally {
      setLoading(false);
    }
  };

  // Debounced live recalc as the slider moves / the input changes
  useEffect(() => {
    if (!itemId) return;
    if (mode === 'absolute' && absoluteCost === '') return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(runSim, 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [percent, absoluteCost, mode, itemId]);

  const totals = result?.totals || {};
  const missingBaseline = totals.missing_baseline;
  const trendIcon = result && result.delta_per_unit >= 0 ? TrendingUp : TrendingDown;
  const trendColor = result && result.delta_per_unit >= 0 ? 'text-red-600' : 'text-emerald-600';

  const sliderColor = useMemo(() => {
    if (percent >= 25) return 'accent-red-600';
    if (percent >= 10) return 'accent-orange-500';
    if (percent <= -10) return 'accent-emerald-600';
    return 'accent-slate-500';
  }, [percent]);

  return (
    <Card className="industrial-card" data-testid="cost-simulator-card">
      <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
        <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-orange-600" /> What-If Cost Simulator
        </h2>
        {result && !missingBaseline && (
          <span className="text-xs text-slate-500 mono">
            Baseline {fmtMoney(result.current_unit_cost)} → {fmtMoney(result.simulated_unit_cost)}
          </span>
        )}
      </CardHeader>
      <CardContent className="p-5 space-y-5">
        {missingBaseline && (
          <div className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 flex items-center gap-2" data-testid="simulator-missing-baseline">
            <AlertTriangle className="w-3 h-3" />
            No baseline unit cost yet — add a price point to enable dollar projections. (Slider still shows the % change.)
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={() => setMode('percent')}
            className={`text-xs px-3 py-1 rounded-sm border ${mode === 'percent' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}
            data-testid="sim-mode-percent"
          >% change</button>
          <button
            onClick={() => setMode('absolute')}
            className={`text-xs px-3 py-1 rounded-sm border ${mode === 'absolute' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}
            data-testid="sim-mode-absolute"
          >absolute $</button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={runSim}
            className="rounded-sm ml-auto"
            data-testid="sim-recalc-btn"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Recalc
          </Button>
        </div>

        {mode === 'percent' ? (
          <div className="space-y-2" data-testid="sim-percent-row">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Hypothetical price change</Label>
              <span className={`mono text-base font-bold ${percent > 0 ? 'text-red-600' : percent < 0 ? 'text-emerald-600' : 'text-slate-700'}`} data-testid="sim-percent-value">
                {percent > 0 ? '+' : ''}{percent}%
              </span>
            </div>
            <input
              type="range"
              min={-50}
              max={200}
              step={1}
              value={percent}
              onChange={(e) => setPercent(parseInt(e.target.value))}
              className={`w-full ${sliderColor}`}
              data-testid="sim-percent-slider"
            />
            <div className="flex justify-between text-[10px] text-slate-400 uppercase tracking-wider">
              <span>−50%</span><span>0</span><span>+200%</span>
            </div>
            <div className="flex gap-2 mt-1">
              {[-25, 0, 10, 25, 50, 100].map(v => (
                <button
                  key={v}
                  onClick={() => setPercent(v)}
                  className="text-xs mono px-2 py-0.5 border border-slate-200 hover:border-orange-400 hover:text-orange-600 rounded-sm"
                  data-testid={`sim-preset-${v}`}
                >{v > 0 ? '+' : ''}{v}%</button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="sim-absolute-row">
            <Label className="text-sm">New unit cost ($)</Label>
            <Input
              type="number" step="0.01" placeholder="e.g. 12.50"
              value={absoluteCost}
              onChange={(e) => setAbsoluteCost(e.target.value)}
              className="rounded-sm mono w-40"
              data-testid="sim-absolute-input"
            />
          </div>
        )}

        {/* Headline KPI */}
        {result && (
          <div className="grid grid-cols-3 gap-3" data-testid="sim-headline">
            <div className="border border-slate-200 rounded-sm p-3">
              <div className="text-xs uppercase text-slate-500">Δ per unit of this SKU</div>
              <div className={`text-lg font-bold mono flex items-center gap-1 ${trendColor}`} data-testid="sim-delta-per-unit">
                {React.createElement(trendIcon, { className: 'w-4 h-4' })}
                {fmtSigned(result.delta_per_unit)} / {baseUom}
              </div>
            </div>
            <div className="border border-slate-200 rounded-sm p-3">
              <div className="text-xs uppercase text-slate-500">Active work orders</div>
              <div className="text-lg font-bold mono" data-testid="sim-active-wo">{totals.active_work_orders}</div>
              <div className="text-xs text-slate-500">{totals.pipeline_remaining_units} units remaining</div>
            </div>
            <div className="border border-slate-200 rounded-sm p-3">
              <div className="text-xs uppercase text-slate-500">Pipeline dollar impact</div>
              <div className={`text-lg font-bold mono ${totals.pipeline_dollar_impact >= 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="sim-pipeline-impact">
                {fmtSigned(totals.pipeline_dollar_impact)}
              </div>
            </div>
          </div>
        )}

        {/* Per-BOM impact table */}
        {result && (
          result.affected_boms.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-4" data-testid="sim-empty">
              {itemName} is not part of any BOM yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Product / BOM</TableHead>
                    <TableHead className="text-right">Qty / Unit</TableHead>
                    <TableHead className="text-right">BOM Cost (now)</TableHead>
                    <TableHead className="text-right">BOM Cost (sim)</TableHead>
                    <TableHead className="text-right">Δ / Assembled Unit</TableHead>
                    <TableHead className="text-right">Active WO</TableHead>
                    <TableHead className="text-right">Pipeline $ Impact</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.affected_boms.map(b => (
                    <TableRow key={b.bom_id} data-testid={`sim-bom-row-${b.bom_id}`}>
                      <TableCell>
                        <div className="font-medium text-slate-800">{b.product_type_name}</div>
                        <div className="text-xs text-slate-500 mono">{b.bom_variant_code} v{b.bom_revision}</div>
                        {b.missing_other_costs && (
                          <div className="text-[10px] uppercase tracking-wider text-amber-600 mt-0.5" data-testid={`sim-missing-${b.bom_id}`}>
                            Other components missing prices
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right mono">{b.qty_per_unit} {b.uom}</TableCell>
                      <TableCell className="text-right mono">{fmtMoney(b.current_bom_component_cost)}</TableCell>
                      <TableCell className="text-right mono">{fmtMoney(b.simulated_bom_component_cost)}</TableCell>
                      <TableCell className={`text-right mono font-semibold ${b.delta_per_assembled_unit >= 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {fmtSigned(b.delta_per_assembled_unit)}
                        {b.delta_pct_of_bom != null && (
                          <span className="text-xs text-slate-400 ml-1">({b.delta_pct_of_bom >= 0 ? '+' : ''}{b.delta_pct_of_bom}%)</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right mono">{b.active_work_orders}</TableCell>
                      <TableCell className={`text-right mono font-semibold ${b.pipeline_dollar_impact >= 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {fmtSigned(b.pipeline_dollar_impact)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
};

export default CostSimulatorCard;
